import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  ArrowBack as BackIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  useContactMessages,
  useDeleteContactMessage,
} from "../../hooks/useContactMessages";
import {
  adminColors,
  dialogStyles,
  tableStyles,
  buttonStyles,
  chipStyles,
} from "./adminStyles";
import { useTranslation } from "react-i18next";

const AdminMessages = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: messages, isLoading, error } = useContactMessages();
  const deleteMessage = useDeleteContactMessage();

  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const handleOpenView = (message) => {
    setSelectedMessage(message);
    setViewDialogOpen(true);
  };

  const handleOpenDelete = (message) => {
    setSelectedMessage(message);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await deleteMessage.mutateAsync(selectedMessage.id);
      setDeleteDialogOpen(false);
    } catch (err) {
      console.error("Error deleting message:", err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          bgcolor: adminColors.bg.page,
        }}
      >
        <CircularProgress sx={{ color: adminColors.accent.teal }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{ minHeight: "100vh", bgcolor: adminColors.bg.page, pt: 12, pb: 8 }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton
              onClick={() => navigate("/admin")}
              sx={{ color: adminColors.text.primary }}
            >
              <BackIcon />
            </IconButton>
            <Typography
              variant="h4"
              sx={{ color: adminColors.text.primary, fontWeight: 700 }}
            >
              {t("admin.sections.messages")}
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error.message}
          </Alert>
        )}

        <TableContainer component={Paper} sx={tableStyles.container}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={tableStyles.headerCell}>
                  {t("admin.labels.name")}
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  {t("admin.labels.email")}
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  {t("admin.labels.subject")}
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  {t("admin.labels.date")}
                </TableCell>
                <TableCell sx={tableStyles.headerCell}>
                  {t("admin.labels.status")}
                </TableCell>
                <TableCell sx={tableStyles.headerCell} align="right">
                  {t("admin.labels.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messages?.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    sx={{
                      ...tableStyles.bodyCell,
                      textAlign: "center",
                      py: 4,
                      color: adminColors.text.secondary,
                    }}
                  >
                    {t("admin.messages.noData")}
                  </TableCell>
                </TableRow>
              )}
              {messages?.map((message) => (
                <TableRow key={message.id} sx={tableStyles.row}>
                  <TableCell sx={tableStyles.bodyCell}>
                    {message.name}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableStyles.bodyCell,
                      color: adminColors.accent.teal,
                    }}
                  >
                    {message.email}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableStyles.bodyCell,
                      color: adminColors.text.secondary,
                      maxWidth: 200,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {message.subject || "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableStyles.bodyCell,
                      color: adminColors.text.secondary,
                    }}
                  >
                    {formatDate(message.created_at)}
                  </TableCell>
                  <TableCell sx={tableStyles.bodyCell}>
                    <Chip
                      label={
                        message.is_read
                          ? t("admin.labels.read")
                          : t("admin.labels.unread")
                      }
                      size="small"
                      sx={
                        message.is_read
                          ? chipStyles.type(adminColors.text.secondary)
                          : chipStyles.type(adminColors.accent.teal)
                      }
                    />
                  </TableCell>
                  <TableCell sx={tableStyles.bodyCell} align="right">
                    <IconButton
                      onClick={() => handleOpenView(message)}
                      sx={buttonStyles.iconEdit}
                    >
                      <ViewIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleOpenDelete(message)}
                      sx={buttonStyles.iconDelete}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog
          open={viewDialogOpen}
          onClose={() => setViewDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: dialogStyles }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: adminColors.text.primary,
              borderBottom: `1px solid ${adminColors.border.default}`,
              pb: 2,
            }}
          >
            <EmailIcon sx={{ color: adminColors.accent.teal }} />
            {t("admin.messages.messageFrom", {
              name: selectedMessage?.name || "",
            })}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ mt: 1 }}>
              <Typography
                variant="body2"
                sx={{ color: adminColors.text.secondary, mb: 1 }}
              >
                <strong style={{ color: adminColors.text.primary }}>
                  {t("admin.labels.email")}:
                </strong>{" "}
                {selectedMessage?.email}
              </Typography>
              {selectedMessage?.phone && (
                <Typography
                  variant="body2"
                  sx={{ color: adminColors.text.secondary, mb: 1 }}
                >
                  <strong style={{ color: adminColors.text.primary }}>
                    {t("admin.labels.phone")}:
                  </strong>{" "}
                  {selectedMessage?.phone}
                </Typography>
              )}
              {selectedMessage?.subject && (
                <Typography
                  variant="body2"
                  sx={{ color: adminColors.text.secondary, mb: 1 }}
                >
                  <strong style={{ color: adminColors.text.primary }}>
                    {t("admin.labels.subject")}:
                  </strong>{" "}
                  {selectedMessage?.subject}
                </Typography>
              )}
              <Typography
                variant="body2"
                sx={{ color: adminColors.text.secondary, mb: 2 }}
              >
                <strong style={{ color: adminColors.text.primary }}>
                  {t("admin.labels.date")}:
                </strong>{" "}
                {formatDate(selectedMessage?.created_at)}
              </Typography>
              <Box
                sx={{
                  bgcolor: adminColors.bg.input,
                  p: 3,
                  borderRadius: 2,
                  mt: 2,
                  border: `1px solid ${adminColors.border.default}`,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: adminColors.text.primary,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {selectedMessage?.message}
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions
            sx={{ p: 3, borderTop: `1px solid ${adminColors.border.default}` }}
          >
            <Button
              href={`mailto:${selectedMessage?.email}`}
              variant="contained"
              startIcon={<EmailIcon />}
              sx={buttonStyles.primary}
            >
              {t("admin.actions.reply")}
            </Button>
            <Button
              onClick={() => setViewDialogOpen(false)}
              sx={buttonStyles.secondary}
            >
              {t("admin.actions.close")}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          PaperProps={{ sx: dialogStyles }}
        >
          <DialogTitle sx={{ color: adminColors.text.primary }}>
            {t("admin.messages.confirmDeleteTitle")}
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: adminColors.text.secondary }}>
              {t("admin.messages.confirmDeleteMessage", {
                name: selectedMessage?.name || "",
              })}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              sx={buttonStyles.secondary}
            >
              {t("admin.actions.cancel")}
            </Button>
            <Button
              onClick={handleDelete}
              variant="contained"
              disabled={deleteMessage.isPending}
              sx={{
                bgcolor: adminColors.status.error,
                "&:hover": { bgcolor: "#DC2626" },
              }}
            >
              {deleteMessage.isPending ? (
                <CircularProgress size={24} />
              ) : (
                t("admin.actions.delete")
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default AdminMessages;
